import { c as _c } from "react/compiler-runtime";
import { closestCenter, defaultDropAnimationSideEffects, DndContext, DragEndEvent, DragOverlay, DropAnimation, MeasuringStrategy, UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { changeFunnelStepPosition } from 'modules/funnels/funnel/api/funnel-api';
import CreateFunnelStepModal from 'modules/funnels/funnel/components/create-funnel-step-modal';
import DraggableFunnelStep from 'modules/funnels/funnel/components/draggable-funnel-step';
import { FunnelStepNavigationCard } from 'modules/funnels/funnel/components/funnel-step-navigation-card';
import InactivePage from 'modules/funnels/funnel/components/inactive-page';
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface';
import { FunnelId } from 'modules/funnels/types/funnel-interface';
import React, { useState } from 'react';
import { useDragSensors } from 'shared/hooks/use-drag-sensors';
import { KeyedMutator } from 'swr';
interface FunnelStepsListProps {
  funnelSteps: FunnelStepInterface[];
  mutate: KeyedMutator<FunnelStepInterface[]>;
  selectedFunnelStep: FunnelStepInterface | null;
  selectFunnelStep: (funnelId: FunnelStepInterface) => void;
  funnelId: FunnelId;
  isFunnelListEmpty: boolean;
}
const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4'
      }
    }
  })
};
function FunnelStepsList(t0) {
  const $ = _c(32);
  const {
    funnelSteps,
    mutate,
    selectedFunnelStep,
    selectFunnelStep,
    funnelId,
    isFunnelListEmpty
  } = t0;
  const [draggableId, setDraggableId] = useState(null);
  const sensors = useDragSensors("item");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async () => {
      setDraggableId(null);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const onDragCancel = t1;
  let t2;
  if ($[1] !== funnelSteps || $[2] !== mutate) {
    t2 = async t3 => {
      const {
        active,
        over
      } = t3;
      if (over && active.id !== over?.id) {
        const activeIndex = funnelSteps.findIndex(t4 => {
          const {
            id
          } = t4;
          return id === active.id;
        });
        const overIndex = funnelSteps.findIndex(t5 => {
          const {
            id: id_0
          } = t5;
          return id_0 === over.id;
        });
        const activeItem = funnelSteps.find(t6 => {
          const {
            id: id_1
          } = t6;
          return id_1 === active.id;
        });
        const overItem = funnelSteps.find(t7 => {
          const {
            id: id_2
          } = t7;
          return id_2 === over.id;
        });
        if (activeItem && overItem && activeIndex !== overIndex) {
          const clonedSteps = [...funnelSteps];
          await mutate(data => {
            if (data) {
              return arrayMove(funnelSteps, activeIndex, overIndex);
            }
          }, false);
          ;
          try {
            await changeFunnelStepPosition(Number(active.id), {
              newPosition: overIndex + 1
            });
          } catch (t8) {
            await mutate(data_0 => {
              if (data_0) {
                return {
                  ...data_0,
                  funnelSteps: clonedSteps
                };
              }
            }, false);
          }
        }
      }
      setDraggableId(null);
    };
    $[1] = funnelSteps;
    $[2] = mutate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onDragEnd = t2;
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      droppable: {
        strategy: MeasuringStrategy.Always
      }
    };
    t4 = t5 => {
      const {
        active: active_0
      } = t5;
      setDraggableId(active_0.id);
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== funnelSteps || $[7] !== selectFunnelStep || $[8] !== selectedFunnelStep) {
    let t6;
    if ($[10] !== selectFunnelStep || $[11] !== selectedFunnelStep) {
      t6 = funnel => <DraggableFunnelStep key={funnel.id} funnelStep={funnel} selectedFunnelStep={selectedFunnelStep} selectFunnelStep={selectFunnelStep} />;
      $[10] = selectFunnelStep;
      $[11] = selectedFunnelStep;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = funnelSteps.map(t6);
    $[6] = funnelSteps;
    $[7] = selectFunnelStep;
    $[8] = selectedFunnelStep;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <div className="flex flex-col" data-base-ui-swipe-ignore={true}>{t5}</div>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== funnelSteps || $[16] !== t6) {
    t7 = <SortableContext items={funnelSteps} strategy={verticalListSortingStrategy}>{t6}</SortableContext>;
    $[15] = funnelSteps;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== isFunnelListEmpty) {
    t8 = !isFunnelListEmpty && <div className="border-border-200 border-b py-3"><CreateFunnelStepModal /></div>;
    $[18] = isFunnelListEmpty;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== funnelId) {
    t9 = <InactivePage funnelId={funnelId} />;
    $[20] = funnelId;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== draggableId || $[23] !== funnelSteps) {
    t10 = draggableId && <DragOverlay dropAnimation={dropAnimationConfig}><FunnelStepNavigationCard render={<div />} className="bg-bg-tertiary" inert={true} funnelStep={funnelSteps.find(item => item.id === draggableId)} /></DragOverlay>;
    $[22] = draggableId;
    $[23] = funnelSteps;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== onDragEnd || $[26] !== sensors || $[27] !== t10 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t11 = <DndContext sensors={sensors} collisionDetection={closestCenter} measuring={t3} onDragStart={t4} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>{t7}{t8}{t9}{t10}</DndContext>;
    $[25] = onDragEnd;
    $[26] = sensors;
    $[27] = t10;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
export default FunnelStepsList;